import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle,
  Globe,
  Palette,
  Bell,
  Shield,
  Database,
  Monitor
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const GeneralSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // State for different settings sections
  const [generalSettings, setGeneralSettings] = useState({});
  const [brandingSettings, setBrandingSettings] = useState({});
  const [localizationSettings, setLocalizationSettings] = useState({});
  const [systemPreferences, setSystemPreferences] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [generalRes, brandingRes, localizationRes, preferencesRes] = await Promise.all([
        MockDataService.getGeneralSettings(),
        MockDataService.getSystemBranding(),
        MockDataService.getLocalizationSettings(),
        MockDataService.getSystemPreferences()
      ]);

      setGeneralSettings(generalRes.data);
      setBrandingSettings(brandingRes.data);
      setLocalizationSettings(localizationRes.data);
      setSystemPreferences(preferencesRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update localStorage for persistence
      localStorage.setItem('generalSettings', JSON.stringify(generalSettings));
      localStorage.setItem('brandingSettings', JSON.stringify(brandingSettings));
      localStorage.setItem('localizationSettings', JSON.stringify(localizationSettings));
      localStorage.setItem('systemPreferences', JSON.stringify(systemPreferences));
      
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setMessage({ type: 'info', text: 'Settings reset to default values' });
  };

  const handleBackup = () => {
    const backup = {
      generalSettings,
      brandingSettings,
      localizationSettings,
      systemPreferences,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settings-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setMessage({ type: 'success', text: 'Settings backup created successfully' });
  };

  const handleRestore = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const backup = JSON.parse(e.target.result);
          setGeneralSettings(backup.generalSettings);
          setBrandingSettings(backup.brandingSettings);
          setLocalizationSettings(backup.localizationSettings);
          setSystemPreferences(backup.systemPreferences);
          setMessage({ type: 'success', text: 'Settings restored successfully' });
        } catch (error) {
          setMessage({ type: 'error', text: 'Invalid backup file' });
        }
      };
      reader.readAsText(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">General Settings</h1>
          <p className="text-gray-600">Configure global system settings and preferences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {message.text && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              General
            </button>
            <button
              onClick={() => setActiveTab('branding')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'branding'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Palette className="h-4 w-4 inline mr-2" />
              Branding
            </button>
            <button
              onClick={() => setActiveTab('localization')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'localization'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Globe className="h-4 w-4 inline mr-2" />
              Localization
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'preferences'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Bell className="h-4 w-4 inline mr-2" />
              Preferences
            </button>
          </nav>
        </div>

        {activeTab === 'general' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="systemName">System Name</Label>
                  <Input
                    id="systemName"
                    value={generalSettings.systemName || ''}
                    onChange={(e) => setGeneralSettings({...generalSettings, systemName: e.target.value})}
                    placeholder="Enter system name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="systemTagline">System Tagline</Label>
                  <Input
                    id="systemTagline"
                    value={generalSettings.systemTagline || ''}
                    onChange={(e) => setGeneralSettings({...generalSettings, systemTagline: e.target.value})}
                    placeholder="Enter system tagline"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="systemVersion">System Version</Label>
                  <Input
                    id="systemVersion"
                    value={generalSettings.systemVersion || ''}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastUpdated">Last Updated</Label>
                  <Input
                    id="lastUpdated"
                    value={generalSettings.lastUpdated ? new Date(generalSettings.lastUpdated).toLocaleString() : ''}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                  <Input
                    id="sessionTimeout"
                    type="number"
                    value={generalSettings.sessionTimeout || 30}
                    onChange={(e) => setGeneralSettings({...generalSettings, sessionTimeout: parseInt(e.target.value)})}
                    min="5"
                    max="480"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxLoginAttempts">Max Login Attempts</Label>
                  <Input
                    id="maxLoginAttempts"
                    type="number"
                    value={generalSettings.maxLoginAttempts || 5}
                    onChange={(e) => setGeneralSettings({...generalSettings, maxLoginAttempts: parseInt(e.target.value)})}
                    min="3"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="enableTwoFactor">Enable Two-Factor Authentication</Label>
                  <p className="text-sm text-gray-600">Require 2FA for all users</p>
                </div>
                <input
                  type="checkbox"
                  id="enableTwoFactor"
                  checked={generalSettings.enableTwoFactor || false}
                  onChange={(e) => setGeneralSettings({...generalSettings, enableTwoFactor: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="enableAuditLog">Enable Audit Logging</Label>
                  <p className="text-sm text-gray-600">Log all system activities</p>
                </div>
                <input
                  type="checkbox"
                  id="enableAuditLog"
                  checked={generalSettings.enableAuditLog || false}
                  onChange={(e) => setGeneralSettings({...generalSettings, enableAuditLog: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Branding & Theme
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      value={brandingSettings.primaryColor || '#3B82F6'}
                      onChange={(e) => setBrandingSettings({...brandingSettings, primaryColor: e.target.value})}
                      className="w-32"
                    />
                    <div 
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: brandingSettings.primaryColor || '#3B82F6' }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="secondaryColor"
                      value={brandingSettings.secondaryColor || '#1E40AF'}
                      onChange={(e) => setBrandingSettings({...brandingSettings, secondaryColor: e.target.value})}
                      className="w-32"
                    />
                    <div 
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: brandingSettings.secondaryColor || '#1E40AF' }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Theme Presets</Label>
                <div className="grid grid-cols-4 gap-2">
                  {brandingSettings.themePresets?.map((preset, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-20 flex flex-col gap-1"
                      onClick={() => {
                        setBrandingSettings({
                          ...brandingSettings,
                          primaryColor: preset.primary,
                          secondaryColor: preset.secondary
                        });
                      }}
                    >
                      <div className="flex gap-1">
                        <div 
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: preset.primary }}
                        />
                        <div 
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: preset.secondary }}
                        />
                      </div>
                      <span className="text-xs">{preset.name}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'localization' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Language & Region
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select
                    value={localizationSettings.language || 'en'}
                    onValueChange={(value) => setLocalizationSettings({...localizationSettings, language: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {localizationSettings.availableLanguages?.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={localizationSettings.timezone || 'UTC'}
                    onValueChange={(value) => setLocalizationSettings({...localizationSettings, timezone: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {localizationSettings.availableTimezones?.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={localizationSettings.currency || 'USD'}
                    onValueChange={(value) => setLocalizationSettings({...localizationSettings, currency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {localizationSettings.availableCurrencies?.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <span className="flex items-center gap-2">
                            <span>{currency.symbol}</span>
                            <span>{currency.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <Select
                    value={localizationSettings.dateFormat || 'MM/DD/YYYY'}
                    onValueChange={(value) => setLocalizationSettings({...localizationSettings, dateFormat: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      <SelectItem value="DD-MM-YYYY">DD-MM-YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableNotifications">Enable Notifications</Label>
                    <p className="text-sm text-gray-600">Allow system notifications</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableNotifications"
                    checked={systemPreferences.enableNotifications || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enableNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableEmailNotifications">Email Notifications</Label>
                    <p className="text-sm text-gray-600">Send notifications via email</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableEmailNotifications"
                    checked={systemPreferences.enableEmailNotifications || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enableEmailNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enablePushNotifications">Push Notifications</Label>
                    <p className="text-sm text-gray-600">Send browser push notifications</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enablePushNotifications"
                    checked={systemPreferences.enablePushNotifications || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enablePushNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                System Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableMaintenanceMode">Maintenance Mode</Label>
                    <p className="text-sm text-gray-600">Put system in maintenance mode</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableMaintenanceMode"
                    checked={systemPreferences.enableMaintenanceMode || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enableMaintenanceMode: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableDebugMode">Debug Mode</Label>
                    <p className="text-sm text-gray-600">Enable debug logging</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableDebugMode"
                    checked={systemPreferences.enableDebugMode || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enableDebugMode: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableAnalytics">Analytics</Label>
                    <p className="text-sm text-gray-600">Collect usage analytics</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableAnalytics"
                    checked={systemPreferences.enableAnalytics || false}
                    onChange={(e) => setSystemPreferences({...systemPreferences, enableAnalytics: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backup & Restore
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBackup}>
              <Download className="h-4 w-4 mr-2" />
              Backup Settings
            </Button>
            <Button variant="outline" asChild>
              <label htmlFor="restore-file" className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Restore Settings
                <input
                  id="restore-file"
                  type="file"
                  accept=".json"
                  onChange={handleRestore}
                  className="hidden"
                />
              </label>
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Create a backup of your current settings or restore from a previous backup file.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralSettings;
